import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, StatusChip, fmtMoney, fmtRelativeTime } from '@smena/ui';
import { Modal } from '@/components/Modal';
import {
  patchKind,
  patchPlan,
  patchSettings,
  resetStaffPassword,
  setStaffActive,
  type AccountCard,
} from './api';

/** Стоп-краны аккаунта: те же четыре флага, что УК видит у себя в «Аккаунте» (Ф6). */
const STOP_FLAGS: Array<{ key: string; label: string; hint: string }> = [
  { key: 'llm_off', label: 'Генерация LLM выключена', hint: 'Конвейер отвечает mock-правилами (L0)' },
  { key: 'autoreply_off', label: 'Автоотправка выключена', hint: 'Все ответы уходят в draft оператору' },
  { key: 'control_off', label: 'Автосообщения Control на паузе', hint: 'Не шлём welcome/чек-ин цепочки' },
  { key: 'cleaners_off', label: 'Наряды клинерам на паузе', hint: 'Не создаём задачи уборки' },
];

export function OverviewTab({ account }: { account: AccountCard }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ProfileCard account={account} />
      <PlanCard account={account} />
      <SettingsCard account={account} />
      <StaffCard account={account} />
    </div>
  );
}

function ProfileCard({ account }: { account: AccountCard }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (kind: 'client' | 'demo') => patchKind(account.id, kind),
    onSuccess: () => {
      toast.success('Тип аккаунта изменён');
      void qc.invalidateQueries({ queryKey: ['account', account.id] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось изменить тип'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Профиль</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        <Row label="Название" value={account.name} />
        <Row label="Slug" value={account.slug} mono />
        <Row label="ID" value={account.id} mono />
        <Row label="Создан" value={new Date(account.createdAt).toLocaleDateString('ru-RU')} />
        <Row
          label="TG-чат оператора"
          value={account.tgOperatorChatId ? String(account.tgOperatorChatId) : 'не задан'}
          mono
        />
        <div className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-3">
          <span className="text-muted">
            Тип
            <span className="ml-2 text-xs">
              (demo разрешает ручной ввод броней, ADR-3)
            </span>
          </span>
          <select
            value={account.kind}
            disabled={mut.isPending}
            onChange={(e) => mut.mutate(e.target.value as 'client' | 'demo')}
            className="h-8 rounded-md border border-line-control bg-input px-2 text-sm text-text"
            aria-label="Тип аккаунта"
          >
            <option value="client">client</option>
            <option value="demo">demo</option>
          </select>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanCard({ account }: { account: AccountCard }) {
  const qc = useQueryClient();
  const [base, setBase] = useState(String(account.plan.base_rub ?? ''));
  const [per, setPer] = useState(String(account.plan.per_property_rub ?? ''));
  const [alpha, setAlpha] = useState(Boolean(account.plan.alpha));

  const mut = useMutation({
    mutationFn: () =>
      patchPlan(account.id, {
        ...(base !== '' ? { base_rub: Number(base) } : {}),
        ...(per !== '' ? { per_property_rub: Number(per) } : {}),
        alpha,
      }),
    onSuccess: () => {
      toast.success('Тариф сохранён');
      void qc.invalidateQueries({ queryKey: ['account', account.id] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить тариф'),
  });

  // Расчётный месячный чек: та же формула, что в /analytics/revenue (факт vs план).
  const mrr =
    (Number(base) || 0) + (Number(per) || 0) * account.counters.properties;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Тариф</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="base_rub">База, ₽/мес</Label>
            <Input
              id="base_rub"
              inputMode="numeric"
              value={base}
              onChange={(e) => setBase(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="per_property_rub">За объект, ₽/мес</Label>
            <Input
              id="per_property_rub"
              inputMode="numeric"
              value={per}
              onChange={(e) => setPer(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={alpha}
            onChange={(e) => setAlpha(e.target.checked)}
            className="size-4 accent-[var(--color-accent)]"
          />
          <span>Альфа-клиент (особые условия)</span>
        </label>
        <div className="flex items-center justify-between border-t border-line pt-3">
          <span className="text-muted">
            Расчётный чек · {account.counters.properties} об.
          </span>
          <span className="font-semibold">{fmtMoney(mrr)}/мес</span>
        </div>
        <Row
          label="Последняя оплата"
          value={account.plan.last_paid_at ? fmtRelativeTime(account.plan.last_paid_at) : 'нет данных'}
        />
        <Button onClick={() => mut.mutate()} disabled={mut.isPending} className="mt-1 self-start">
          {mut.isPending ? 'Сохраняем…' : 'Сохранить тариф'}
        </Button>
      </CardContent>
    </Card>
  );
}

function SettingsCard({ account }: { account: AccountCard }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (patch: Record<string, boolean>) => patchSettings(account.id, patch),
    onSuccess: () => {
      toast.success('Настройка применена');
      void qc.invalidateQueries({ queryKey: ['account', account.id] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось применить'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Стоп-краны</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-xs text-muted">
          Действуют мгновенно на конвейер этого аккаунта. Клиент увидит изменение в своём аудите.
        </p>
        {STOP_FLAGS.map((f) => {
          const on = Boolean(account.settings[f.key]);
          return (
            <label key={f.key} className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={on}
                disabled={mut.isPending}
                onChange={(e) => mut.mutate({ [f.key]: e.target.checked })}
                className="mt-0.5 size-4 accent-[var(--color-accent)]"
              />
              <span>
                <span className={on ? 'text-amber' : ''}>{f.label}</span>
                <span className="block text-xs text-muted">{f.hint}</span>
              </span>
            </label>
          );
        })}
      </CardContent>
    </Card>
  );
}

function StaffCard({ account }: { account: AccountCard }) {
  const qc = useQueryClient();
  const [reset, setReset] = useState<{ email: string; password: string } | null>(null);

  const resetMut = useMutation({
    mutationFn: (uid: string) => resetStaffPassword(account.id, uid),
    onSuccess: (r) => setReset(r),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось сбросить пароль'),
  });
  const activeMut = useMutation({
    mutationFn: ({ uid, active }: { uid: string; active: boolean }) =>
      setStaffActive(account.id, uid, active),
    onSuccess: () => {
      toast.success('Статус пользователя изменён');
      void qc.invalidateQueries({ queryKey: ['account', account.id] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось изменить статус'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Пользователи панели</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {account.staffUsers.length === 0 ? (
          <p className="text-muted">Пользователей нет - клиент не сможет войти в панель.</p>
        ) : null}
        {account.staffUsers.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-3 border-b border-line/60 pb-2 last:border-0">
            <div className="min-w-0">
              <div className="truncate">
                {u.name}{' '}
                <StatusChip tone={u.role === 'owner' ? 'accent' : 'muted'}>{u.role}</StatusChip>
                {!u.active ? (
                  <StatusChip tone="red" className="ml-1">
                    выключен
                  </StatusChip>
                ) : null}
              </div>
              <div className="truncate font-mono text-[11px] text-muted">{u.email}</div>
              <div className="text-[11px] text-muted">вход: {fmtRelativeTime(u.lastLoginAt)}</div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={resetMut.isPending}
                onClick={() => resetMut.mutate(u.id)}
              >
                Сбросить пароль
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={activeMut.isPending}
                onClick={() => activeMut.mutate({ uid: u.id, active: !u.active })}
              >
                {u.active ? 'Выключить' : 'Включить'}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
      {reset ? (
        <Modal title="Пароль сброшен" onClose={() => setReset(null)}>
          <p className="mb-3 text-sm text-muted">
            Передайте клиенту. Показывается один раз - в базе только хеш.
          </p>
          <div className="rounded-md border border-line bg-panel2 p-3 font-mono text-sm">
            <div>{reset.email}</div>
            <div className="text-accent-fg">{reset.password}</div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(`${reset.email} / ${reset.password}`);
                toast.success('Скопировано');
              }}
            >
              Скопировать
            </Button>
            <Button onClick={() => setReset(null)}>Готово</Button>
          </div>
        </Modal>
      ) : null}
    </Card>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-muted">{label}</span>
      <span className={mono ? 'truncate font-mono text-xs' : 'truncate'}>{value}</span>
    </div>
  );
}
